import { createFileRoute, type ToOptions } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import z from 'zod'

import { typedObjectEntries } from '@gozoki/tools'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { RealEstateCalendarTab } from '~/components/routes/development/real-estate/calendar/real-estate-calendar-tab'
import { RealEstateContactsTab } from '~/components/routes/development/real-estate/contacts/real-estate-contacts-tab'
import { RealEstateEnseignesTab } from '~/components/routes/development/real-estate/enseignes/real-estate-enseignes-tab'
import { RealEstateInterestZonesTab } from '~/components/routes/development/real-estate/interest-zones/real-estate-interest-zones-tab'
import { KanbanFullArea } from '~/components/routes/development/real-estate/kanban/kanban-full-area'
import { RealEstateMapTab } from '~/components/routes/development/real-estate/map/real-estate-map-tab'
import { StoreProjectSearchFilterCreate } from '~/components/routes/development/real-estate/store-project/store-project-search-filter-create'
import type { ProjectStatus } from '~/components/routes/development/real-estate/store-project/types'
import { RealEstateTasksTab } from '~/components/routes/development/real-estate/tasks/tasks-tab'
import { useRealEstatePermissions } from '~/components/routes/development/real-estate/use-real-estate-permissions'

const tabValues = [
  'map',
  'kanban',
  'tasks',
  'calendar',
  'contacts',
  'enseignes',
  'interest-zones',
] as const
const REAL_ESTATE_LAST_TAB_STORAGE_KEY = 'gozoki.backOffice.realEstate.lastTab'

export const Route = createFileRoute('/_authenticated/development/real-estate/$tab')({
  component: RealEstatePage,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
})

const tabOptions = {
  map: {
    label: 'Carte',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'map' } },
    Component: RealEstateMapTab,
  },
  kanban: {
    label: 'Kanban',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'kanban' } },
    Component: KanbanFullArea,
  },
  tasks: {
    label: 'Tâches',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'tasks' } },
    Component: RealEstateTasksTab,
  },
  calendar: {
    label: 'Calendrier',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'calendar' } },
    Component: RealEstateCalendarTab,
  },
  contacts: {
    label: 'Contacts',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'contacts' } },
    Component: RealEstateContactsTab,
  },
  enseignes: {
    label: 'Enseignes concurrentes',
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'enseignes' } },
    Component: RealEstateEnseignesTab,
  },
  'interest-zones': {
    label: "Zones d'intérêt",
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'interest-zones' } },
    Component: RealEstateInterestZonesTab,
  },
} as const satisfies Record<
  (typeof tabValues)[number],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { toOptions: ToOptions; Component: React.ComponentType<any>; label: string }
>

function RealEstatePage() {
  const realEstatePermissions = useRealEstatePermissions()

  const navigate = Route.useNavigate()
  const { tab } = Route.useParams()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>([])

  const newProjectHandlerRef = useRef<(() => void) | null>(null)
  const registerNewProjectHandler = useRef((handler: (() => void) | null): void => {
    newProjectHandlerRef.current = handler
  }).current

  useEffect(() => {
    try {
      window.localStorage.setItem(REAL_ESTATE_LAST_TAB_STORAGE_KEY, tab)
    } catch {
      // ignore (private mode / blocked storage)
    }
  }, [tab])

  const isProjectFilterTab = tab === 'map' || tab === 'kanban'
  const [headerSlotContent, setHeaderSlotContent] = useState<React.ReactNode>(null)

  function renderCurrentTab(): React.ReactNode {
    if (tab === 'map') {
      return (
        <RealEstateMapTab
          key={tab}
          searchQuery={searchQuery}
          selectedStatuses={selectedStatuses}
          onRegisterNewProject={registerNewProjectHandler}
        />
      )
    }
    if (tab === 'kanban') {
      return (
        <KanbanFullArea
          key={tab}
          searchQuery={searchQuery}
          selectedStatuses={selectedStatuses}
          onRegisterNewProject={registerNewProjectHandler}
        />
      )
    }
    if (tab === 'tasks') {
      return <RealEstateTasksTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    if (tab === 'calendar') {
      return <RealEstateCalendarTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    if (tab === 'contacts') {
      return <RealEstateContactsTab key={tab} onHeaderChange={setHeaderSlotContent} />
    }
    // Fallback : tabs sans props additionnelles (enseignes, interest-zones, …).
    const TabComponent = tabOptions[tab].Component
    return <TabComponent key={tab} />
  }

  const showHeaderSlot = tab === 'tasks' || tab === 'calendar' || tab === 'contacts'

  if (!realEstatePermissions.canRead) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Accès refusé</h3>
          <p>Vous devez avoir un rôle Foncier & travaux pour accéder à cette page.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppHeader className="mb-4">
        <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
          Développement foncier
        </Title>
      </AppHeader>

      <AppContainer>
        <div style={{ marginBottom: '20px' }} className="flex justify-between items-center">
          <AppTabs
            tabs={typedObjectEntries(tabOptions).map(([key, value]) => ({
              label: value.label,
              value: key,
            }))}
            value={tab}
            onTabChange={(v) =>
              navigate({ ...tabOptions[v].toOptions, search: (prev) => ({ ...prev }) })
            }
          />
          {isProjectFilterTab && (
            <StoreProjectSearchFilterCreate
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onNewProject={() => newProjectHandlerRef.current?.()}
              canManage={realEstatePermissions.canCreateProject()}
              selectedStatuses={selectedStatuses}
              onStatusFilterChange={setSelectedStatuses}
            />
          )}
          {showHeaderSlot && headerSlotContent}
        </div>

        {renderCurrentTab()}
      </AppContainer>
    </>
  )
}
